import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

const NAV_LINKS = [
  { label: 'How it works', href: '#how' },
  { label: 'For schools',  href: '#schools' },
  { label: 'For instructors', href: '#instructors' },
  { label: 'Road safety', href: '#safety' },
  { label: 'Pricing',     href: '#pricing' },
]

const FAULT_ROWS = [
  { label: 'Observation & blind spots', val: 2,  max: 10, color: '#0F7C6C' },
  { label: 'Lane changes',              val: 1,  max: 10, color: '#0F7C6C' },
  { label: 'Parallel parking',          val: 6,  max: 10, color: '#D4972B' },
  { label: 'Right-of-way judgement',    val: 0,  max: 10, color: '#0F7C6C' },
]

const CHAPTERS = [
  { tag: 'Ch 1–2', title: 'Vehicle control & cockpit drill', body: 'Mirrors, seatbelt, SIPDE — build the pre-drive habit before touching the key.' },
  { tag: 'Ch 3–4', title: 'Rules of the road', body: 'Signs, signals, right-of-way, intersections and lane discipline.' },
  { tag: 'Ch 5–6', title: 'Hazard perception', body: 'Scan patterns, following distance, urban risk identification.' },
  { tag: 'Ch 7',   title: 'Highway & merging', body: 'Entry/exit ramps, blind-spot checks, lane changes at speed.' },
  { tag: 'Ch 8–9', title: 'Winter, rain & night driving', body: 'Traction management, reduced visibility protocols, stopping distances.' },
  { tag: 'Ch 10',  title: 'Impairment, fatigue & the law', body: 'Ontario HTA, zero-tolerance rules, graduated licensing obligations.' },
]

export default function Home() {
  const navigate = useNavigate()
  const [instructors, setInstructors] = useState(4)
  const [seats,       setSeats]       = useState(12)
  const [mobMenuOpen, setMobMenuOpen] = useState(false)

  const freeSeats   = instructors * 3
  const licenceCost = instructors * 9.99
  const seatCost    = seats * 2.99
  const monthOne    = licenceCost + Math.max(0, seats - freeSeats) * 2.99
  const ongoing     = licenceCost + seatCost
  const perStudent  = seats > 0 ? ongoing / seats : null

  const fmt = (n: number) => `$${n.toFixed(2)}`

  return (
    <div style={{ fontFamily: 'Inter,sans-serif', background: '#FAF6EF', color: '#1B2A4A' }}>

      {/* ── Sticky Header ── */}
      <header style={{
        position: 'sticky', top: 0, zIndex: 20,
        height: 76, display: 'flex', alignItems: 'center',
        padding: '0 32px', justifyContent: 'space-between',
        background: 'rgba(250,246,239,0.92)', backdropFilter: 'blur(10px)',
        borderBottom: '1px solid rgba(27,42,74,0.08)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }} onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
          <img src='/logo-light.png' alt='DriveTrack' height={68} style={{ display: 'block' }} />
        </div>
        <nav className='lnav-links'>
          {NAV_LINKS.map(l => (
            <a key={l.label} href={l.href} style={{ font: '500 14px Inter,sans-serif', color: 'rgba(27,42,74,0.7)', textDecoration: 'none' }}
              onMouseEnter={e => (e.currentTarget.style.color = '#1B2A4A')}
              onMouseLeave={e => (e.currentTarget.style.color = 'rgba(27,42,74,0.7)')}>
              {l.label}
            </a>
          ))}
          <button onClick={() => navigate('/login')} style={{ font: '600 14px Inter,sans-serif', color: '#1B2A4A', background: 'none', border: 'none', cursor: 'pointer', padding: '8px 0' }}>Log in</button>
          <button onClick={() => navigate('/register')} style={{
            font: '600 13.5px Inter,sans-serif', color: '#fff', background: '#1B2A4A',
            border: 'none', borderRadius: 9, padding: '10px 18px', cursor: 'pointer',
          }}
            onMouseEnter={e => (e.currentTarget.style.background = '#243761')}
            onMouseLeave={e => (e.currentTarget.style.background = '#1B2A4A')}>
            Start free trial
          </button>
        </nav>
        {/* Mobile-only header actions */}
        <div className='lnav-mob'>
          <button onClick={() => navigate('/login')} style={{ font: '600 13px Inter,sans-serif', color: '#1B2A4A', background: 'none', border: 'none', cursor: 'pointer' }}>Log in</button>
          <button onClick={() => navigate('/register')} style={{ font: '600 13px Inter,sans-serif', color: '#fff', background: '#1B2A4A', border: 'none', borderRadius: 8, padding: '9px 14px', cursor: 'pointer' }}>Sign up</button>
          <button
            onClick={() => setMobMenuOpen(o => !o)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 22, color: '#1B2A4A', padding: '4px 2px', lineHeight: 1 }}
            aria-label='Menu'
          >{mobMenuOpen ? '✕' : '☰'}</button>
        </div>
      </header>

      {/* Mobile nav drawer — deliberately a SIBLING of <header>, not nested inside it.
          <header> itself has backdropFilter for its glass-morphism effect, and per the CSS
          spec, filter/backdrop-filter/transform on an ancestor creates a new containing
          block for any position:fixed descendant. Nesting this drawer inside header made it
          fixed relative to header's own 76px-tall box instead of the viewport, collapsing
          its height to almost nothing and letting the hero section show through — the exact
          overlapping-text bug this was written to fix. */}
      {mobMenuOpen && (
        <div style={{
          position: 'fixed', top: 76, left: 0, right: 0, bottom: 0, zIndex: 999,
          background: '#FAF6EF', backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
          display: 'flex', flexDirection: 'column', padding: '24px 24px',
          borderTop: '1px solid rgba(27,42,74,0.08)', overflowY: 'auto',
        } as React.CSSProperties}>
          {NAV_LINKS.map(l => (
            <a
              key={l.label}
              href={l.href}
              onClick={() => setMobMenuOpen(false)}
              style={{
                font: '500 18px Inter,sans-serif', color: '#1B2A4A',
                textDecoration: 'none', padding: '14px 0',
                borderBottom: '1px solid rgba(27,42,74,0.07)',
              }}
            >{l.label}</a>
          ))}
          <div style={{ marginTop: 28, display: 'flex', flexDirection: 'column', gap: 12 }}>
            <button onClick={() => { setMobMenuOpen(false); navigate('/login') }} style={{ font: '600 15px Inter,sans-serif', color: '#1B2A4A', background: 'rgba(27,42,74,0.07)', border: 'none', borderRadius: 10, padding: '13px', cursor: 'pointer' }}>Log in</button>
            <button onClick={() => { setMobMenuOpen(false); navigate('/register') }} style={{ font: '600 15px Inter,sans-serif', color: '#fff', background: '#1B2A4A', border: 'none', borderRadius: 10, padding: '13px', cursor: 'pointer' }}>Start free trial</button>
          </div>
        </div>
      )}

      {/* ── Hero ── */}
      <section className='hero-wrap'>
        <div>
          <div style={{ display: 'inline-block', padding: '6px 13px', borderRadius: 100, background: 'rgba(15,124,108,0.1)', border: '1px solid rgba(15,124,108,0.25)', font: '600 11.5px Inter,sans-serif', color: '#0F7C6C', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 22 }}>
            Built on the MTO G2 scoring sheet
          </div>
          <h1 className='hero-h' style={{ textWrap: 'pretty' } as React.CSSProperties}>
            More of your students pass the G2 the first time.
          </h1>
          <p style={{ font: '400 17px/1.62 Inter,sans-serif', color: 'rgba(27,42,74,0.65)', maxWidth: 520, margin: '0 0 28px' }}>
            DriveTrack gives driving schools and independent instructors a shared road-test playbook: track every in-car skill, run full mock G2 tests with real fault categories, and only certify students the data says are ready.
          </p>
          <div className='hero-ctas'>
            <button onClick={() => navigate('/register')} style={{ font: '600 15px Inter,sans-serif', color: '#fff', background: '#0F7C6C', border: 'none', borderRadius: 10, padding: '15px 26px', cursor: 'pointer' }}
              onMouseEnter={e => (e.currentTarget.style.background = '#0c6355')}
              onMouseLeave={e => (e.currentTarget.style.background = '#0F7C6C')}>
              Start free — first month
            </button>
            <button style={{ font: '600 15px Inter,sans-serif', color: '#1B2A4A', background: '#fff', border: '1px solid rgba(27,42,74,0.15)', borderRadius: 10, padding: '15px 26px', cursor: 'pointer' }}>
              See a live demo
            </button>
          </div>
          <div style={{ font: '400 13px Inter,sans-serif', color: 'rgba(27,42,74,0.45)', marginBottom: 34 }}>
            First month free · 3 student seats per instructor included
          </div>
          <div className='hero-stats'>
            {[
              { num: '88%',  label: 'First-attempt G2 pass rate\nacross DriveTrack schools' },
              { num: '310+', label: 'Schools and independent\ninstructors on the platform' },
              { num: '41k',  label: 'In-car lessons logged\nskill-by-skill' },
            ].map(s => (
              <div key={s.num}>
                <div style={{ font: '700 30px Lora,serif', color: '#1B2A4A' }}>{s.num}</div>
                <div style={{ font: '400 12.5px/1.4 Inter,sans-serif', color: 'rgba(27,42,74,0.55)', whiteSpace: 'pre-line', marginTop: 3 }}>{s.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Product preview card */}
        <div className='hero-card' style={{ background: '#1B2A4A', borderRadius: 18, padding: 22, boxShadow: '0 30px 60px -25px rgba(27,42,74,0.45)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 14 }}>
            {['#E0663B', '#D4972B', '#0F7C6C'].map(c => <div key={c} style={{ width: 9, height: 9, borderRadius: '50%', background: c }} />)}
            <span style={{ font: '400 12px Inter,sans-serif', color: 'rgba(255,255,255,0.5)', marginLeft: 6 }}>Mock G2 test · Priya Raman</span>
          </div>
          <div style={{ background: '#fff', borderRadius: 12, padding: '18px 18px 14px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14 }}>
              <div>
                <div style={{ font: '400 11px Inter,sans-serif', color: 'rgba(27,42,74,0.45)', marginBottom: 2 }}>Readiness score</div>
                <div style={{ font: '700 34px/1 Lora,serif', color: '#0F7C6C' }}>84<span style={{ font: '400 16px/1 Inter,sans-serif', color: 'rgba(27,42,74,0.4)' }}>/100</span></div>
              </div>
              <div style={{ padding: '5px 10px', borderRadius: 100, background: 'rgba(15,124,108,0.12)', font: '600 11px Inter,sans-serif', color: '#0F7C6C' }}>Cleared to certify</div>
            </div>
            {FAULT_ROWS.map(r => (
              <div key={r.label} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 9 }}>
                <span style={{ font: '400 12.5px Inter,sans-serif', color: '#1B2A4A', flex: 1 }}>{r.label}</span>
                <div style={{ width: 110, height: 6, borderRadius: 3, background: 'rgba(27,42,74,0.08)', flexShrink: 0 }}>
                  <div style={{ width: `${(r.val / r.max) * 100}%`, height: '100%', borderRadius: 3, background: r.color }} />
                </div>
                <span style={{ font: '400 11.5px Inter,sans-serif', color: 'rgba(27,42,74,0.5)', width: 34, textAlign: 'right' }}>{r.val}/{r.max}</span>
              </div>
            ))}
            <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
              <button style={{ flex: 1, background: '#1B2A4A', color: '#fff', border: 'none', borderRadius: 9, padding: '11px 0', font: '600 13px Inter,sans-serif', cursor: 'pointer' }}>Issue certificate</button>
              <button style={{ padding: '11px 16px', background: '#fff', color: '#1B2A4A', border: '1px solid rgba(27,42,74,0.15)', borderRadius: 9, font: '600 13px Inter,sans-serif', cursor: 'pointer' }}>Re-test</button>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 10 }}>
            {[['CURRICULUM', '18 / 20 h online'], ['IN-CAR', '9 / 10 h logged']].map(([label, val]) => (
              <div key={label} style={{ background: 'rgba(255,255,255,0.07)', borderRadius: 10, padding: '12px 14px' }}>
                <div style={{ font: '600 9.5px Inter,sans-serif', color: 'rgba(255,255,255,0.45)', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: 4 }}>{label}</div>
                <div style={{ font: '600 14px Inter,sans-serif', color: '#fff' }}>{val}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Problem Band ── */}
      <section className='lp-band' style={{ background: '#1B2A4A' }}>
        <div className='lp-band-inner'>
          <div style={{ font: '600 13px Inter,sans-serif', color: '#D4972B', textTransform: 'uppercase', letterSpacing: '1.2px', marginBottom: 14 }}>The problem</div>
          <h2 style={{ font: '600 34px/1.25 Lora,serif', color: '#fff', maxWidth: 720, margin: '0 0 32px' }}>
            Nearly half of Ontario road-test candidates fail their first attempt — and the school gets the blame.
          </h2>
          <div className='problem-grid'>
            {[
              { stat: '~45%', title: 'fail their first attempt', body: 'Ontario pass rates haven\'t improved in a decade despite better cars, better roads, and more training options.' },
              { stat: '3 wks', title: 'average re-test wait', body: 'A failed test means three weeks of lost momentum, a second booking fee, and a frustrated student.' },
              { stat: '0', title: 'records most schools keep', body: 'Without outcome logs, schools can\'t identify weak instructors, failing patterns, or liability exposure.' },
            ].map(c => (
              <div key={c.stat} style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 14, padding: 24 }}>
                <div style={{ font: '700 26px Lora,serif', color: '#D4972B', marginBottom: 6 }}>{c.stat}</div>
                <div style={{ font: '600 15px Inter,sans-serif', color: '#fff', marginBottom: 8 }}>{c.title}</div>
                <div style={{ font: '400 13.5px/1.55 Inter,sans-serif', color: 'rgba(255,255,255,0.62)' }}>{c.body}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── How it works ── */}
      <section id='how' className='lp-section'>
        <div style={{ font: '600 13px Inter,sans-serif', color: 'rgba(27,42,74,0.4)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: 12 }}>How it works</div>
        <h2 style={{ font: '600 34px/1.25 Lora,serif', color: '#1B2A4A', marginBottom: 32 }}>Four steps from enrolment to road test.</h2>
        <div className='how-grid'>
          {[
            { n: '1', title: 'Enroll', body: 'Capture G1 details and expiry, assign an instructor, and set the lesson plan.' },
            { n: '2', title: 'Teach & log', body: 'Tick each skill on the MTO checklist as taught; hours and gaps track themselves.' },
            { n: '3', title: 'Mock G2 test', body: 'Score a full practice test on the real fault categories, live pass/fail math.' },
            { n: '4', title: 'Certify & log outcome', body: 'Only certify students the score clears; record the real G2 result.' },
          ].map(c => (
            <div key={c.n} style={{ background: '#fff', border: '1px solid rgba(27,42,74,0.08)', borderRadius: 14, padding: 24 }}>
              <div style={{ width: 32, height: 32, borderRadius: 9, background: 'rgba(15,124,108,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', font: '700 14px Inter,sans-serif', color: '#0F7C6C', marginBottom: 14 }}>{c.n}</div>
              <div style={{ font: '600 16px Inter,sans-serif', color: '#1B2A4A', marginBottom: 8 }}>{c.title}</div>
              <div style={{ font: '400 13.5px/1.6 Inter,sans-serif', color: 'rgba(27,42,74,0.62)' }}>{c.body}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ── Audience Panels ── */}
      <section className='audience-grid'>
        {/* Schools */}
        <div id='schools' style={{ background: '#fff', borderRadius: 18, padding: 34 }}>
          <div style={{ display: 'inline-block', padding: '4px 12px', borderRadius: 100, background: 'rgba(27,42,74,0.07)', font: '600 11.5px Inter,sans-serif', color: '#1B2A4A', marginBottom: 16 }}>For driving schools</div>
          <h3 style={{ font: '600 24px/1.35 Lora,serif', color: '#1B2A4A', margin: '0 0 10px' }}>Run every instructor off one road-test standard.</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, margin: '18px 0 24px' }}>
            {['Instructor scorecards: pass rate and average readiness per teacher', 'Seat pooling and billing across the roster', 'Road safety course: 20 h online plus in-car lesson tracking', 'Certificate issuing gated on mock-test results', 'Audit-ready outcome logs and exportable reports'].map(f => (
              <div key={f} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                <div style={{ width: 18, height: 18, borderRadius: '50%', background: 'rgba(15,124,108,0.13)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, font: '600 10px Inter', color: '#0F7C6C', marginTop: 1 }}>✓</div>
                <span style={{ font: '400 14px/1.5 Inter,sans-serif', color: '#1B2A4A' }}>{f}</span>
              </div>
            ))}
          </div>
          <button onClick={() => navigate('/register?role=school')} style={{ background: '#1B2A4A', color: '#fff', border: 'none', borderRadius: 10, padding: '13px 22px', font: '600 14px Inter,sans-serif', cursor: 'pointer', width: '100%' }}
            onMouseEnter={e => (e.currentTarget.style.background = '#243761')}
            onMouseLeave={e => (e.currentTarget.style.background = '#1B2A4A')}>
            Register your school
          </button>
        </div>

        {/* Instructors */}
        <div id='instructors' style={{ background: '#fff', borderRadius: 18, padding: 34 }}>
          <div style={{ display: 'inline-block', padding: '4px 12px', borderRadius: 100, background: 'rgba(212,151,43,0.15)', font: '600 11.5px Inter,sans-serif', color: '#8a5f10', marginBottom: 16 }}>For independent instructors</div>
          <h3 style={{ font: '600 24px/1.35 Lora,serif', color: '#1B2A4A', margin: '0 0 10px' }}>Look like a full school, on your own.</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, margin: '18px 0 24px' }}>
            {['Enroll a student in under two minutes', 'Full mock G2 test with live pass/fail scoring', 'Automatic readiness score per skill', 'Issue completion certificates without a school behind you', 'Students get their own progress portal'].map(f => (
              <div key={f} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                <div style={{ width: 18, height: 18, borderRadius: '50%', background: 'rgba(212,151,43,0.18)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, font: '600 10px Inter', color: '#8a5f10', marginTop: 1 }}>✓</div>
                <span style={{ font: '400 14px/1.5 Inter,sans-serif', color: '#1B2A4A' }}>{f}</span>
              </div>
            ))}
          </div>
          <button onClick={() => navigate('/register?role=instructor')} style={{ background: '#D4972B', color: '#1B2A4A', border: 'none', borderRadius: 10, padding: '13px 22px', font: '600 14px Inter,sans-serif', cursor: 'pointer', width: '100%' }}>
            Register as an instructor
          </button>
        </div>
      </section>

      {/* ── Road Safety Curriculum ── */}
      <section id='safety' className='lp-band' style={{ background: '#F2ECE1', borderTop: '1px solid rgba(27,42,74,0.07)', borderBottom: '1px solid rgba(27,42,74,0.07)' }}>
        <div className='lp-band-inner'>
          <h2 style={{ font: '600 34px/1.25 Lora,serif', color: '#1B2A4A', maxWidth: 680, margin: '0 0 12px' }}>Passing the test is the by-product. Safe drivers are the point.</h2>
          <p style={{ font: '400 15px/1.6 Inter,sans-serif', color: 'rgba(27,42,74,0.65)', maxWidth: 620, margin: '0 0 36px' }}>A 20-hour online road safety course with chapter quizzes whose scores feed directly into each student's readiness score — so only students who've understood the theory get certified.</p>
          <div className='safety-grid'>
            {CHAPTERS.map(c => (
              <div key={c.tag} style={{ background: '#fff', borderRadius: 13, padding: '20px 22px' }}>
                <div style={{ font: '600 10.5px Inter,sans-serif', color: 'rgba(27,42,74,0.4)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 }}>{c.tag}</div>
                <div style={{ font: '600 16px Inter,sans-serif', color: '#1B2A4A', marginBottom: 7 }}>{c.title}</div>
                <div style={{ font: '400 13px/1.55 Inter,sans-serif', color: 'rgba(27,42,74,0.62)' }}>{c.body}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Pricing ── */}
      <section id='pricing' className='lp-section'>
        <div style={{ textAlign: 'center', marginBottom: 40 }}>
          <h2 style={{ font: '600 34px/1.25 Lora,serif', color: '#1B2A4A', margin: '0 0 10px' }}>$9.99 per instructor. $2.99 per student seat.</h2>
          <p style={{ font: '400 15px/1.6 Inter,sans-serif', color: 'rgba(27,42,74,0.6)', maxWidth: 600, margin: '0 auto' }}>Every instructor gets 3 student seats free for their first month. A seat covers one student from enrollment through their G2 outcome log.</p>
        </div>
        <div className='g3price'>
          {/* Instructor licence */}
          <div style={{ background: '#fff', border: '1px solid rgba(27,42,74,0.1)', borderRadius: 16, padding: 30, display: 'flex', flexDirection: 'column' }}>
            <div style={{ font: '600 13px Inter,sans-serif', color: 'rgba(27,42,74,0.5)', marginBottom: 8 }}>Instructor licence</div>
            <div style={{ font: '700 30px Lora,serif', color: '#1B2A4A', marginBottom: 4 }}>$9.99</div>
            <div style={{ font: '400 13px Inter,sans-serif', color: 'rgba(27,42,74,0.5)', marginBottom: 20 }}>/ instructor / month</div>
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 24 }}>
              {['Unlimited lessons and skill checklists', 'Mock G2 test and readiness scoring', 'Course completion certificates', 'Cancel or add instructors any month'].map(f => (
                <div key={f} style={{ display: 'flex', gap: 9 }}>
                  <span style={{ color: '#0F7C6C', flexShrink: 0 }}>✓</span>
                  <span style={{ font: '400 13.5px/1.5 Inter,sans-serif', color: '#1B2A4A' }}>{f}</span>
                </div>
              ))}
            </div>
            <button onClick={() => navigate('/register')} style={{ background: '#1B2A4A', color: '#fff', border: 'none', borderRadius: 9, padding: '12px 0', font: '600 14px Inter,sans-serif', cursor: 'pointer', width: '100%' }}>Start free month</button>
          </div>

          {/* Student seats */}
          <div style={{ background: '#1B2A4A', borderRadius: 16, padding: 30, display: 'flex', flexDirection: 'column' }}>
            <div style={{ font: '600 13px Inter,sans-serif', color: '#D4972B', marginBottom: 8 }}>Student seats</div>
            <div style={{ font: '700 30px Lora,serif', color: '#fff', marginBottom: 4 }}>$2.99</div>
            <div style={{ font: '400 13px Inter,sans-serif', color: 'rgba(255,255,255,0.5)', marginBottom: 8 }}>/ seat / month</div>
            <div style={{ font: '400 13px/1.5 Inter,sans-serif', color: 'rgba(255,255,255,0.62)', marginBottom: 20 }}>First 3 seats per instructor are free for your first month — then $2.99 each.</div>
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 24 }}>
              {['3 free seats per instructor in month one', '20-hour online road safety course', 'Progress and readiness dashboard', 'Seats released when a student finishes', 'Seats pool across a school roster'].map(f => (
                <div key={f} style={{ display: 'flex', gap: 9 }}>
                  <span style={{ color: '#7ee0cd', flexShrink: 0 }}>✓</span>
                  <span style={{ font: '400 13.5px/1.5 Inter,sans-serif', color: 'rgba(255,255,255,0.88)' }}>{f}</span>
                </div>
              ))}
            </div>
            <button onClick={() => navigate('/register')} style={{ background: '#0F7C6C', color: '#fff', border: 'none', borderRadius: 9, padding: '12px 0', font: '600 14px Inter,sans-serif', cursor: 'pointer', width: '100%' }}>Start free month</button>
          </div>

          {/* Calculator */}
          <div style={{ background: '#fff', border: '1px solid rgba(27,42,74,0.1)', borderRadius: 16, padding: 30 }}>
            <div style={{ font: '600 13px Inter,sans-serif', color: 'rgba(27,42,74,0.5)', marginBottom: 16 }}>Cost calculator</div>

            {[
              { label: 'Instructors', value: instructors, min: 1, max: 50, step: 1, set: setInstructors },
              { label: 'Student seats', value: seats, min: 0, max: 400, step: 3, set: setSeats },
            ].map(({ label, value, min, max, step, set }) => (
              <div key={label} style={{ marginBottom: 18 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                  <span style={{ font: '500 13px Inter,sans-serif', color: '#1B2A4A' }}>{label}</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <button onClick={() => set(Math.max(min, value - step))} style={{ width: 24, height: 24, borderRadius: 6, border: '1px solid rgba(27,42,74,0.15)', background: '#fff', font: '600 14px Inter', color: '#1B2A4A', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>−</button>
                    <span style={{ font: '600 16px Lora,serif', color: '#1B2A4A', minWidth: 28, textAlign: 'center' }}>{value}</span>
                    <button onClick={() => set(Math.min(max, value + step))} style={{ width: 24, height: 24, borderRadius: 6, border: '1px solid rgba(27,42,74,0.15)', background: '#fff', font: '600 14px Inter', color: '#1B2A4A', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>+</button>
                  </div>
                </div>
                {label === 'Student seats' && (
                  <input type='range' min={0} max={150} step={1} value={Math.min(seats, 150)} onChange={e => setSeats(Number(e.target.value))}
                    style={{ width: '100%', accentColor: '#0F7C6C' }} />
                )}
              </div>
            ))}

            <div style={{ background: 'rgba(15,124,108,0.1)', border: '1px solid rgba(15,124,108,0.25)', borderRadius: 10, padding: '12px 14px', marginBottom: 10 }}>
              <div style={{ font: '400 12px Inter,sans-serif', color: '#0F7C6C', marginBottom: 4 }}>Month one total</div>
              <div style={{ font: '700 22px Lora,serif', color: '#0F7C6C' }}>{fmt(monthOne)}</div>
              <div style={{ font: '400 11.5px Inter,sans-serif', color: 'rgba(15,124,108,0.8)', marginTop: 2 }}>{instructors * 3} free seats applied</div>
            </div>
            <div style={{ background: 'rgba(27,42,74,0.05)', borderRadius: 10, padding: '12px 14px' }}>
              <div style={{ font: '400 12px Inter,sans-serif', color: 'rgba(27,42,74,0.55)', marginBottom: 4 }}>Ongoing monthly</div>
              <div style={{ font: '700 22px Lora,serif', color: '#1B2A4A' }}>{fmt(ongoing)}</div>
              {perStudent !== null && <div style={{ font: '400 11.5px Inter,sans-serif', color: 'rgba(27,42,74,0.5)', marginTop: 2 }}>{fmt(perStudent)} per student seat</div>}
            </div>
          </div>
        </div>
      </section>

      {/* ── Testimonial ── */}
      <section className='lp-section' style={{ paddingTop: 0 }}>
        <div className='testi-wrap'>
          <div>
            <p className='testi-q' style={{ margin: '0 0 20px' }}>
              "Our first-attempt pass rate went from 61% to 88% in two cohorts. Every instructor is running off the same checklist now — there are no more surprises on test day."
            </p>
            <div style={{ font: '600 14px Inter,sans-serif', color: '#1B2A4A' }}>Marcus Grewal</div>
            <div style={{ font: '400 13px Inter,sans-serif', color: 'rgba(27,42,74,0.55)' }}>Owner, Maple Grove Driving School · Brampton, ON</div>
          </div>
          <div className='testi-stats'>
            <div>
              <div style={{ font: '700 30px Lora,serif', color: '#0F7C6C' }}>+27 pts</div>
              <div style={{ font: '400 13px Inter,sans-serif', color: 'rgba(27,42,74,0.55)', marginTop: 3 }}>first-attempt pass rate gain</div>
            </div>
            <div>
              <div style={{ font: '700 30px Lora,serif', color: '#0F7C6C' }}>2 cohorts</div>
              <div style={{ font: '400 13px Inter,sans-serif', color: 'rgba(27,42,74,0.55)', marginTop: 3 }}>to reach 88% average</div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Final CTA Band ── */}
      <section className='lp-band' style={{ background: '#0F7C6C', textAlign: 'center' }}>
        <div style={{ maxWidth: 840, margin: '0 auto' }}>
          <h2 style={{ font: '600 36px/1.2 Lora,serif', color: '#fff', margin: '0 0 14px' }}>Start with one cohort. Keep the pass rate.</h2>
          <p style={{ font: '400 16px/1.6 Inter,sans-serif', color: 'rgba(255,255,255,0.75)', margin: '0 0 32px' }}>$9.99 / instructor · $2.99 / seat · First month free</p>
          <div className='cta-btns'>
            <button onClick={() => navigate('/register')} style={{ background: '#fff', color: '#0F7C6C', border: 'none', borderRadius: 10, padding: '15px 28px', font: '600 15px Inter,sans-serif', cursor: 'pointer' }}>Create an account</button>
            <button onClick={() => navigate('/login')} style={{ background: 'rgba(255,255,255,0.14)', color: '#fff', border: '1px solid rgba(255,255,255,0.3)', borderRadius: 10, padding: '15px 28px', font: '600 15px Inter,sans-serif', cursor: 'pointer' }}>Log in</button>
          </div>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer style={{ background: '#1B2A4A', padding: '44px 32px' }}>
        <div className='footer-inner'>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <img src='/logo-icon.png' alt='' height={52} style={{ display: 'block' }} />
            <span style={{ font: '600 18px/1 Lora,serif', color: '#fff' }}>DriveTrack</span>
          </div>
          <div style={{ font: '400 13px Inter,sans-serif', color: 'rgba(255,255,255,0.45)' }}>
            Ontario G2 road-test aligned · Not affiliated with the Ministry of Transportation · © 2026 DriveTrack Inc.
          </div>
        </div>
      </footer>
    </div>
  )
}
